import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Plus,
    FileText,
    Clock,
    Trash2,
    Sparkles,
    Link as LinkIcon,
    Copy,
    X,
    Rocket,
    Check,
    ChevronRight,
    Activity,
    AlertCircle,
    Edit2,
    Send,
    Archive
} from 'lucide-react';
import { Project, ProjectStatus, MilestoneStatus, UserRole, TeamMember, Milestone } from '../types';
import { Button, StatusBadge, Input, TextArea, Modal, Switch } from './UIComponents';
import { generateMilestones } from '../services/geminiService';

interface DashboardProps {
    projects: Project[];
    onSelectProject: (id: string) => void;
    onCreateProject: (project: Partial<Project>) => Promise<Project | undefined>;
    role: UserRole;
    autoOpenCreate: boolean;
    teamMembers: TeamMember[];
    title?: string;
    onBulkDeleteProjects?: (ids: string[]) => Promise<void>;
    onBulkRestoreProjects?: (ids: string[]) => Promise<void>;
    editingProject?: Project;
    onUpdateProjectAndSend?: (projectId: string, project: Partial<Project>) => Promise<Project | undefined>;
    onCloseEdit?: () => void;
    customNavigate?: (path: string) => void;
    customLocation?: { pathname: string };
}

const getNextDeadline = (project: Project) => {
    if (project.status === ProjectStatus.COMPLETED || project.status === ProjectStatus.ARCHIVED || project.status === ProjectStatus.DRAFT) return null;
    let cursor = project.startDate ? new Date(project.startDate).getTime() : Date.now();
    for (const m of project.milestones) {
        const isComplete = m.status === MilestoneStatus.APPROVED;
        if (isComplete && m.completedAt) {
            cursor = new Date(m.completedAt).getTime();
        } else if (isComplete) {
            cursor += m.duration * 86400000;
        } else {
            cursor = Math.max(cursor, Date.now());
            const due = cursor + m.duration * 86400000;
            return new Date(due);
        }
    }
    return null;
}

const ListRow: React.FC<{
    project: Project;
    onSelect: (id: string) => void;
    isSelected?: boolean;
    onToggleSelect?: (id: string) => void;
    showCheckbox?: boolean;
}> = ({ project, onSelect, isSelected = false, onToggleSelect, showCheckbox = false }) => {
    const nextDeadline = getNextDeadline(project);
    const total = project.milestones.length;
    const completed = project.milestones.filter(m => m.status === MilestoneStatus.APPROVED).length;
    const progress = total === 0 ? 0 : Math.round((completed / total) * 100);
    const activeMilestone = project.milestones.find(m => m.status === MilestoneStatus.IN_PROGRESS || m.status === MilestoneStatus.REVIEW)
        || project.milestones.find(m => m.status === MilestoneStatus.NOT_STARTED);

    const gridCols = showCheckbox
        ? 'md:grid-cols-[40px_3fr_1.5fr_1.2fr_1.2fr_100px]'
        : 'md:grid-cols-[3fr_1.5fr_1.2fr_1.2fr_100px]';

    return (
        <div
            onClick={() => onSelect(project.id)}
            className={`group flex flex-col md:grid ${gridCols} gap-3 md:gap-4 py-4 px-4 border-b border-zinc-200 dark:border-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-900/50 cursor-pointer transition-all md:items-center first:border-t first:border-zinc-200 dark:first:border-zinc-900`}
        >
            {showCheckbox && (
                <div className="hidden md:flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                            e.stopPropagation();
                            onToggleSelect?.(project.id);
                        }}
                        className="w-4 h-4 rounded border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-zinc-500 dark:focus:ring-zinc-400 bg-white dark:bg-zinc-900 cursor-pointer"
                    />
                </div>
            )}
            <div className="flex items-start gap-4 min-w-0">
                {showCheckbox && (
                    <div className="md:hidden flex items-center" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                                e.stopPropagation();
                                onToggleSelect?.(project.id);
                            }}
                            className="w-4 h-4 rounded border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-zinc-500 dark:focus:ring-zinc-400 bg-white dark:bg-zinc-900 cursor-pointer"
                        />
                    </div>
                )}
                <div className="w-9 h-9 rounded bg-white dark:bg-zinc-900 flex items-center justify-center text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-600 dark:group-hover:text-zinc-300 transition-colors border border-zinc-200 dark:border-zinc-800 shrink-0 mt-1 md:mt-0">
                    <Activity size={16} strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="text-[14px] font-medium text-zinc-900 dark:text-zinc-200 block truncate transition-colors mb-0.5 tracking-tight">{project.name}</h3>
                    <span className="text-[11px] text-zinc-500 block truncate flex items-center gap-1.5">
                        {activeMilestone ? (
                            <>
                                <span className={`w-1.5 h-1.5 rounded-full ${activeMilestone.status === MilestoneStatus.REVIEW ? 'bg-amber-500' : 'bg-emerald-500'}`}></span>
                                {activeMilestone.title}
                            </>
                        ) : (
                            project.status === ProjectStatus.DRAFT ? 'Draft - Setup in progress' : 'All milestones completed'
                        )}
                    </span>
                </div>
            </div>

            {/* Mobile: Layout specific for small screens */}
            <div className="md:contents flex items-center justify-between pl-[52px] md:pl-0 gap-4">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-6 h-6 rounded-full bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center text-[10px] text-zinc-500 border border-zinc-200 dark:border-zinc-800 shrink-0 font-medium">
                        {project.receiverName.charAt(0)}
                    </div>
                    <span className="text-[13px] text-zinc-600 dark:text-zinc-500 truncate group-hover:text-zinc-800 dark:group-hover:text-zinc-400 transition-colors">{project.receiverName}</span>
                </div>

                <div className="md:hidden"><StatusBadge status={project.status} /></div>
            </div>

            <div className="hidden md:block"><StatusBadge status={project.status} /></div>

            <div className="md:contents hidden">
                <div className="flex items-center gap-3 pr-4">
                    <div className="flex-1 h-1 bg-zinc-100 dark:bg-zinc-900 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-500 ${progress === 100 ? 'bg-blue-500' : 'bg-zinc-400 dark:bg-zinc-700'}`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="text-[10px] text-zinc-500 dark:text-zinc-600 font-mono w-6 text-right">{progress}%</span>
                </div>

                <div className="text-right">
                    {nextDeadline ? (
                        <span className={`text-[12px] font-mono ${nextDeadline < new Date() ? 'text-rose-500 dark:text-rose-400' : 'text-zinc-500'}`}>
                            {nextDeadline.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                    ) : (
                        <span className="text-[12px] text-zinc-400 dark:text-zinc-700 font-mono">--</span>
                    )}
                </div>
            </div>
        </div>
    )
}

export const Dashboard: React.FC<DashboardProps> = ({
    projects,
    onSelectProject,
    onCreateProject,
    role,
    autoOpenCreate,
    teamMembers,
    title = "Active Projects",
    onBulkDeleteProjects,
    onBulkRestoreProjects,
    editingProject,
    onUpdateProjectAndSend,
    onCloseEdit,
    customNavigate,
    customLocation
}) => {
    const navigate = customNavigate || useNavigate();
    const location = customLocation || useLocation();

    // Derive archive state from URL instead of local state
    const showArchived = location.pathname.includes('/dashboard/archive');
    const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
    const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    // Sync modal state with route - check for role-based routes
    const isCreatingFromRoute = location.pathname.includes('/projects/new');
    const isEditMode = !!editingProject;
    const [isCreating, setIsCreating] = useState(autoOpenCreate || isCreatingFromRoute || isEditMode);

    // Sync modal state when route changes (browser back/forward)
    useEffect(() => {
        if (isCreatingFromRoute && !isCreating) {
            setIsCreating(true);
        } else if (!isCreatingFromRoute && isCreating && !autoOpenCreate && !isEditMode) {
            setIsCreating(false);
        }
    }, [isCreatingFromRoute, isCreating, autoOpenCreate, isEditMode]);

    // Open modal automatically when editingProject is provided
    useEffect(() => {
        if (editingProject && !isCreating) {
            setIsCreating(true);
        }
    }, [editingProject]);
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const [isGenerating, setIsGenerating] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [isLaunching, setIsLaunching] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        receiverName: '',
        receiverEmail: '',
        description: "",
        startDate: new Date().toISOString().split('T')[0]
    });

    const [includeOnboarding, setIncludeOnboarding] = useState(true);
    const [questions, setQuestions] = useState<{ id: string, text: string, required: boolean }[]>([
        { id: 'q1', text: 'What is the primary goal for this project?', required: true }
    ]);
    const [milestones, setMilestones] = useState<Milestone[]>([]);
    const [generatedLink, setGeneratedLink] = useState('');

    // Email state
    const [emailMessage, setEmailMessage] = useState('');
    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // Pre-fill form data when editingProject is provided
    useEffect(() => {
        if (editingProject) {
            setFormData({
                name: editingProject.name || '',
                receiverName: editingProject.receiverName || '',
                receiverEmail: editingProject.receiverEmail || '',
                description: editingProject.description || '',
                startDate: editingProject.startDate ? new Date(editingProject.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
            });
            setQuestions(editingProject.onboardingQuestions && editingProject.onboardingQuestions.length > 0
                ? editingProject.onboardingQuestions.map(q => ({ id: q.id, text: q.text, required: q.required }))
                : [{ id: 'q1', text: 'What is the primary goal for this project?', required: true }]
            );
            setMilestones(editingProject.milestones || []);
            setIncludeOnboarding(!!(editingProject.onboardingQuestions && editingProject.onboardingQuestions.length > 0));
            // Initialize email message for edit mode
            const firstName = editingProject.receiverName.split(' ')[0] || 'there';
            setEmailMessage(`Hi ${firstName},\n\nI've set up a project console for ${editingProject.name}. Please click the link below to answer a few quick questions so we can get started:\n\n${window.location.origin}/onboard/${editingProject.id}`);
            setGeneratedLink(`${window.location.origin}/onboard/${editingProject.id}`);
            setStep(1);
            setIsCreating(true);
        }
    }, [editingProject]);

    const handleNextStep = async (skipOnboarding: boolean = false) => {
        if (step === 1) {
            const nameOk = formData.name.trim().length > 0;
            const receiverNameOk = formData.receiverName.trim().length > 0;
            const emailOk = /\S+@\S+\.\S+/.test(formData.receiverEmail.trim());
            if (!nameOk || !receiverNameOk || !emailOk) {
                setFormError('Please add project name, receiver name, and a valid receiver email.');
                return;
            }
            setFormError(null);
            setStep(2);
        } else if (step === 2) {
            const effectiveIncludeOnboarding = !skipOnboarding;

            if (milestones.length === 0 && formData.name.length > 3) {
                setIsGenerating(true);
                const generated = await generateMilestones(formData.name);

                // If onboarding is enabled, milestones are DRAFT. If skipped, they are NOT_STARTED (with first IN_PROGRESS)
                const status = effectiveIncludeOnboarding ? MilestoneStatus.DRAFT : MilestoneStatus.NOT_STARTED;
                const draftMilestones = generated.map((m, idx) => ({
                    ...m,
                    status: (!effectiveIncludeOnboarding && idx === 0) ? MilestoneStatus.IN_PROGRESS : status
                }));

                setMilestones(draftMilestones);
                setIsGenerating(false);
            } else if (milestones.length > 0) {
                // Ensure milestone status reflects current choice (in case user went Back and toggled skip)
                const status = effectiveIncludeOnboarding ? MilestoneStatus.DRAFT : MilestoneStatus.NOT_STARTED;
                const updatedMilestones = milestones.map((m, idx) => ({
                    ...m,
                    status: (!effectiveIncludeOnboarding && idx === 0) ? MilestoneStatus.IN_PROGRESS : status
                }));
                setMilestones(updatedMilestones);
            }

            setIncludeOnboarding(effectiveIncludeOnboarding);
            setStep(3);
        } else if (step === 3) {
            const nameOk = formData.name.trim().length > 0;
            const receiverNameOk = formData.receiverName.trim().length > 0;
            const emailOk = /\S+@\S+\.\S+/.test(formData.receiverEmail.trim());
            if (!nameOk || !receiverNameOk || !emailOk) {
                setStep(1);
                setFormError('Please add project name, receiver name, and a valid receiver email.');
                return;
            }
            setFormError(null);
            // Initialize email message for step 4
            const link = `https://verline.studio/invite/${Math.random().toString(36).substr(2, 6)}`;
            setGeneratedLink(link);
            const firstName = formData.receiverName.split(' ')[0] || 'there';
            setEmailMessage(`Hi ${firstName},\n\nI've set up a project console for ${formData.name}. Please click the link below to answer a few quick questions so we can get started:\n\n${link}`);
            setIsEditingEmail(false);
            setStep(4);
        }
    };

    const handleLaunch = async () => {
        setIsLaunching(true);
        setFormError(null);

        try {
            const finalProject: Partial<Project> = {
                name: formData.name.trim(),
                receiverName: formData.receiverName.trim(),
                receiverEmail: formData.receiverEmail.trim(),
                description: formData.description,
                milestones: milestones,
                // If onboarding is skipped, we don't save the questions
                onboardingQuestions: includeOnboarding ? questions : [],
                startDate: new Date(formData.startDate).toISOString(),
                // If onboarding is included, project is WAITING_ON_RECEIVER. If skipped, project is ACTIVE immediately
                status: includeOnboarding ? ProjectStatus.WAITING_ON_RECEIVER : ProjectStatus.ACTIVE
            };

            let updatedProject: Project | undefined;

            if (isEditMode && editingProject && onUpdateProjectAndSend) {
                updatedProject = await onUpdateProjectAndSend(editingProject.id, finalProject);
            } else {
                updatedProject = await onCreateProject(finalProject);
            }

            if (updatedProject) {
                // Close modal and reset form first
                setIsCreating(false);
                setStep(1);
                setFormData({
                    name: '',
                    receiverName: '',
                    receiverEmail: '',
                    description: "",
                    startDate: new Date().toISOString().split('T')[0]
                });
                setMilestones([]);
                setIncludeOnboarding(true);
                setQuestions([{ id: 'q1', text: 'What is the primary goal for this project?', required: true }]);

                // Clear edit state if in edit mode
                if (isEditMode) {
                    onCloseEdit?.();
                }

                // Navigate to role-based dashboard first (only for new projects)
                if (!isEditMode && location.pathname.includes('/projects/new')) {
                    const rolePath = location.pathname.startsWith('/receiver/') ? 'receiver' : 'provider';
                    navigate(`/${rolePath}/dashboard`);
                }

                // Then select the project to navigate to project view
                onSelectProject(updatedProject.id);
            } else {
                setFormError(isEditMode ? 'Failed to update project. Please try again.' : 'Failed to create project. Please try again.');
            }
        } catch (error) {
            console.error('Error creating/updating project:', error);
            setFormError(isEditMode ? 'Failed to update project. Please try again.' : 'Failed to create project. Please try again.');
        } finally {
            setIsLaunching(false);
        }
    };

    const handleCloseCreate = () => {
        setIsCreating(false);
        setStep(1);
        setFormData({
            name: '',
            receiverName: '',
            receiverEmail: '',
            description: "",
            startDate: new Date().toISOString().split('T')[0]
        });
        setMilestones([]);
        setIncludeOnboarding(true);
        setQuestions([{ id: 'q1', text: 'What is the primary goal for this project?', required: true }]);

        // Clear edit state if in edit mode
        if (isEditMode) {
            onCloseEdit?.();
        }

        // Navigate back to role-based dashboard if we're on /projects/new route (only for new projects)
        if (!isEditMode && location.pathname.includes('/projects/new')) {
            const rolePath = location.pathname.startsWith('/receiver/') ? 'receiver' : 'provider';
            navigate(`/${rolePath}/dashboard`);
        }
    };

    // Clear selection when switching away from archived view
    useEffect(() => {
        if (!showArchived) {
            setSelectedProjectIds(new Set());
        }
    }, [showArchived]);

    const handleToggleSelect = (projectId: string) => {
        setSelectedProjectIds(prev => {
            const next = new Set(prev);
            if (next.has(projectId)) {
                next.delete(projectId);
            } else {
                next.add(projectId);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (visibleProjects.length === 0) return;
        const allSelectedCheck = visibleProjects.every(p => selectedProjectIds.has(p.id));
        if (allSelectedCheck) {
            setSelectedProjectIds(new Set());
        } else {
            setSelectedProjectIds(new Set(visibleProjects.map(p => p.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (!onBulkDeleteProjects || selectedProjectIds.size === 0) return;
        setIsDeleteConfirmOpen(true);
    };

    const handleConfirmBulkDelete = async () => {
        if (!onBulkDeleteProjects || selectedProjectIds.size === 0) return;
        setIsBulkActionLoading(true);
        try {
            await onBulkDeleteProjects(Array.from(selectedProjectIds));
            setSelectedProjectIds(new Set());
            setIsDeleteConfirmOpen(false);
        } catch (error) {
            console.error('Error deleting projects:', error);
        } finally {
            setIsBulkActionLoading(false);
        }
    };

    const handleBulkUnarchive = async () => {
        if (!onBulkRestoreProjects || selectedProjectIds.size === 0) return;
        setIsBulkActionLoading(true);
        try {
            await onBulkRestoreProjects(Array.from(selectedProjectIds));
            setSelectedProjectIds(new Set());
        } catch (error) {
            console.error('Error restoring projects:', error);
        } finally {
            setIsBulkActionLoading(false);
        }
    };

    // Filter by archive status only - workspace filtering is handled at App level through visibleProjects prop
    const visibleProjects = projects.filter(p => showArchived ? p.status === ProjectStatus.ARCHIVED : p.status !== ProjectStatus.ARCHIVED);
    const allSelected = visibleProjects.length > 0 && visibleProjects.every(p => selectedProjectIds.has(p.id));

    const StepIndicator = ({ num, label, isActive, isCompleted }: { num: number, label: string, isActive: boolean, isCompleted: boolean }) => (
        <div className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${isActive ? 'bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800' : 'opacity-40'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium border ${isActive || isCompleted ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-950 border-transparent' : 'bg-transparent text-zinc-400 border-zinc-300 dark:border-zinc-700'}`}>
                {isCompleted ? <Check size={12} /> : num}
            </div>
            <span className={`text-[13px] ${isActive ? 'text-zinc-900 dark:text-zinc-100 font-medium' : 'text-zinc-500'} hidden md:inline`}>{label}</span>
        </div>
    );

    return (
        <div className="w-full h-full flex flex-col animate-fade-in bg-white dark:bg-black transition-colors duration-200">
            <div className="pt-6 md:pt-10 px-4 md:px-10 mb-6 md:mb-8 flex flex-col md:flex-row md:items-end justify-between shrink-0 gap-4">
                <div>
                    <h1 className="text-2xl font-medium text-zinc-900 dark:text-zinc-100 tracking-tight mb-1">{showArchived ? 'Archive' : title}</h1>
                    <p className="text-zinc-500 text-[13px]">{visibleProjects.length} Projects</p>
                </div>

                <div className="flex items-center gap-4">
                    <button
                        onClick={() => {
                            const rolePath = location.pathname.startsWith('/receiver/') ? 'receiver' : 'provider';
                            navigate(showArchived ? `/${rolePath}/dashboard` : `/${rolePath}/dashboard/archive`);
                        }}
                        className={`text-[12px] font-medium transition-colors ${showArchived ? 'text-zinc-800 dark:text-zinc-200' : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-400'}`}
                    >
                        {showArchived ? 'Active Projects' : 'Archive'}
                    </button>
                    {role === UserRole.PROVIDER && !showArchived && (
                        <Button
                            id="dashboard-new-project-btn"
                            onClick={() => {
                                // #region agent log
                                fetch('http://127.0.0.1:7243/ingest/02fb56fd-40d8-458d-939a-d486f51f7d8b', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Dashboard.tsx:268', message: 'New Project button clicked - navigating to /projects/new', data: { currentPath: window.location.pathname }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'run1', hypothesisId: 'H3' }) }).catch(() => { });
                                // #endregion
                                const rolePath = location.pathname.startsWith('/receiver/') ? 'receiver' : 'provider';
                                navigate(`/${rolePath}/projects/new`);
                            }} variant="primary" size="sm" className="ml-2">
                            <Plus size={14} strokeWidth={1.5} className="mr-1.5" /> New Project
                        </Button>
                    )}
                </div>
            </div>

            <Modal isOpen={isCreating} onClose={handleCloseCreate} title={isEditMode ? "Complete & Send Project" : "New Project"} maxWidth="max-w-4xl" noPadding>
                <div className="flex flex-col md:flex-row h-[600px] md:h-[560px] w-full bg-white dark:bg-zinc-950 relative">
                    <Button
                        variant="ghost"
                        onClick={handleCloseCreate}
                        className="absolute top-4 right-4 z-20 p-1 md:block hidden"
                    >
                        <X size={18} />
                    </Button>

                    {/* Close button for mobile */}
                    <Button
                        variant="ghost"
                        onClick={handleCloseCreate}
                        className="absolute top-2 right-2 z-20 p-2 md:hidden block"
                    >
                        <X size={18} />
                    </Button>

                    <div className="w-full md:w-60 bg-white dark:bg-zinc-950 border-b md:border-b-0 md:border-r border-zinc-200 dark:border-zinc-900 flex md:flex-col p-4 md:p-6 shrink-0 gap-4 md:gap-0 overflow-x-auto md:overflow-visible no-scrollbar items-center md:items-stretch">
                        <div className="md:mb-8 shrink-0">
                            <h2 className="text-[16px] font-medium text-zinc-900 dark:text-zinc-100 leading-tight truncate max-w-[150px] md:max-w-none">{formData.name || 'Untitled Project'}</h2>
                        </div>
                        <div className="flex md:flex-col gap-2 md:gap-2 flex-1 md:space-y-2">
                            <StepIndicator num={1} label="Details" isActive={step === 1} isCompleted={step > 1} />
                            <StepIndicator num={2} label="Questions" isActive={step === 2} isCompleted={step > 2} />
                            <StepIndicator num={3} label="Timeline" isActive={step === 3} isCompleted={step > 3} />
                            <StepIndicator num={4} label="Launch" isActive={step === 4} isCompleted={step > 4} />
                        </div>
                    </div>

                    <div className="flex-1 flex flex-col min-w-0 bg-zinc-50 dark:bg-black">
                        <div className="flex-1 overflow-y-auto p-6 md:p-8">
                            {formError && (
                                <div className="mb-4 text-[12px] text-amber-300 bg-amber-900/20 border border-amber-800 rounded px-3 py-2">
                                    {formError}
                                </div>
                            )}
                            {step === 1 && (
                                <div className="space-y-6 max-w-lg mx-auto">
                                    <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Project Details</h3>
                                    <div className="space-y-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Name</label>
                                            <Input autoFocus placeholder="e.g. Q4 Rebrand" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-1.5">
                                                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Receiver Name</label>
                                                <Input placeholder="Acme Inc." value={formData.receiverName} onChange={(e) => setFormData({ ...formData, receiverName: e.target.value })} />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Receiver Email</label>
                                                <Input placeholder="contact@acme.com" value={formData.receiverEmail} onChange={(e) => setFormData({ ...formData, receiverEmail: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Project Description</label>
                                            <TextArea
                                                className="h-24"
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Welcome to your project console. This is your single source of truth for our progress, timeline, and deliveries."
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="space-y-6 max-w-lg mx-auto">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Onboarding Questions</h3>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleNextStep(true)}
                                            className="text-[11px] flex items-center gap-1"
                                        >
                                            Skip <ChevronRight size={12} />
                                        </Button>
                                    </div>

                                    <div>
                                        <p className="text-[13px] text-zinc-500 mb-6">Ask the receiver 1-3 essential questions before starting.</p>
                                        <div className="space-y-3">
                                            {questions.map((q, idx) => (
                                                <div key={q.id} className="flex items-start gap-3 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 p-3 rounded-md group">
                                                    <div className="flex-1 space-y-2">
                                                        <Input className="bg-transparent" value={q.text} onChange={(e) => { const nQ = [...questions]; nQ[idx].text = e.target.value; setQuestions(nQ); }} />
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] text-zinc-500 uppercase">Required</span>
                                                            <Switch checked={q.required} onChange={(val) => { const nQ = [...questions]; nQ[idx].required = val; setQuestions(nQ); }} />
                                                        </div>
                                                    </div>
                                                    <Button variant="ghost" size="sm" onClick={() => setQuestions(prev => prev.filter((_, i) => i !== idx))} className="text-zinc-400 hover:text-red-500 p-1"><Trash2 size={14} /></Button>
                                                </div>
                                            ))}
                                            {questions.length < 3 && (
                                                <Button variant="ghost" size="sm" onClick={() => setQuestions([...questions, { id: Math.random().toString(), text: '', required: true }])} className="text-[12px] flex items-center gap-2">
                                                    <Plus size={14} /> Add Question
                                                </Button>
                                            )}
                                        </div>
                                        <div className="mt-8 pt-4 border-t border-zinc-200 dark:border-zinc-900">
                                            <p className="flex items-center gap-2 text-[12px] text-zinc-500">
                                                <AlertCircle size={14} className="text-emerald-500" />
                                                Receiver answers will be used to finalize the project milestones.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {step === 3 && (
                                <div className="space-y-6 max-w-xl mx-auto">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Milestone Timeline</h3>
                                        <Button size="sm" variant="secondary" onClick={async () => {
                                            setIsGenerating(true);
                                            const gen = await generateMilestones(formData.name);
                                            const status = includeOnboarding ? MilestoneStatus.DRAFT : MilestoneStatus.NOT_STARTED;
                                            setMilestones(gen.map((m, idx) => ({
                                                ...m,
                                                status: (!includeOnboarding && idx === 0) ? MilestoneStatus.IN_PROGRESS : status
                                            })));
                                            setIsGenerating(false);
                                        }}>
                                            <Sparkles size={14} className={`mr-2 ${isGenerating ? 'animate-spin' : ''}`} /> Generate
                                        </Button>
                                    </div>

                                    {includeOnboarding ? (
                                        <div className="bg-zinc-100 dark:bg-zinc-900/30 border border-dashed border-zinc-300 dark:border-zinc-800 rounded p-3 mb-4 flex items-start gap-3">
                                            <div className="w-1.5 h-1.5 rounded-full bg-zinc-400 dark:bg-zinc-500 mt-1.5 shrink-0"></div>
                                            <div>
                                                <div className="text-[13px] font-medium text-zinc-700 dark:text-zinc-300">Draft — will be finalized after receiver onboarding</div>
                                                <div className="text-[11px] text-zinc-500 mt-1">Receivers will see these as a preview. You can adjust them after you receive their questionnaire answers.</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/30 rounded p-3 mb-4 flex items-start gap-3">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0"></div>
                                            <div>
                                                <div className="text-[13px] font-medium text-emerald-800 dark:text-emerald-100">Live Timeline</div>
                                                <div className="text-[11px] text-emerald-600 dark:text-emerald-500/80 mt-1">Receivers will see these milestones immediately upon joining.</div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-3">
                                        {milestones.map((m, idx) => (
                                            <div key={m.id} className="flex items-center gap-3 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 p-3 rounded-md">
                                                <div className="flex-1 min-w-0">
                                                    <Input className="bg-transparent" value={m.title} onChange={(e) => { const nM = [...milestones]; nM[idx].title = e.target.value; setMilestones(nM); }} />
                                                </div>
                                                <div className="flex items-center gap-2 text-[11px] text-zinc-500 shrink-0">
                                                    <Clock size={12} />
                                                    <Input type="number" className="w-12 bg-transparent text-center" value={m.duration} onChange={(e) => { const nM = [...milestones]; nM[idx].duration = parseInt(e.target.value) || 0; setMilestones(nM); }} />d
                                                </div>
                                                <Button variant="ghost" size="sm" onClick={() => setMilestones(prev => prev.filter(x => x.id !== m.id))} className="text-zinc-400 hover:text-red-500 shrink-0 p-1"><X size={14} /></Button>
                                            </div>
                                        ))}
                                        <Button variant="ghost" size="sm" onClick={() => {
                                            const status = includeOnboarding ? MilestoneStatus.DRAFT : MilestoneStatus.NOT_STARTED;
                                            setMilestones([...milestones, { id: Math.random().toString(), title: "", status, duration: 3, comments: [] }])
                                        }} className="text-[12px] flex items-center gap-2">
                                            <Plus size={14} /> Add Milestone
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {step === 4 && (
                                <div className="flex flex-col items-center justify-center h-full text-center max-w-lg mx-auto">
                                    <Rocket size={40} className="text-emerald-500 mb-4" />
                                    <h2 className="text-xl font-medium text-zinc-900 dark:text-zinc-100 mb-2">Ready to Launch</h2>
                                    <p className="text-zinc-500 text-sm mb-8">Send this to {formData.receiverName} to begin onboarding.</p>

                                    <div className="w-full bg-zinc-50 dark:bg-zinc-900/30 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 text-left mb-6">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Email Message</span>
                                            <div className="flex items-center gap-2">
                                                {isEditingEmail ? (
                                                    <Button size="sm" variant="secondary" className="h-6 text-[11px]" onClick={() => setIsEditingEmail(false)}>
                                                        Save
                                                    </Button>
                                                ) : (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setIsEditingEmail(true)}
                                                            className="text-[11px] flex items-center gap-1.5"
                                                        >
                                                            <Edit2 size={12} /> Edit
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="primary"
                                                            className="h-7 text-[11px] min-w-[80px]"
                                                            onClick={async () => {
                                                                setIsSending(true);
                                                                // Placeholder for platform sending
                                                                await new Promise(r => setTimeout(r, 1500));
                                                                setIsSending(false);
                                                                alert(`Invitation sent to ${formData.receiverEmail} from Verline.`);
                                                            }}
                                                            isLoading={isSending}
                                                        >
                                                            <Send size={12} className="mr-1.5" /> Send
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {isEditingEmail ? (
                                            <TextArea
                                                className="h-32 font-mono"
                                                value={emailMessage}
                                                onChange={(e) => setEmailMessage(e.target.value)}
                                            />
                                        ) : (
                                            <div className="text-[13px] text-zinc-600 dark:text-zinc-300 leading-relaxed font-mono bg-white dark:bg-zinc-950 p-3 rounded border border-zinc-200 dark:border-zinc-800 whitespace-pre-wrap">
                                                {emailMessage}
                                            </div>
                                        )}
                                    </div>

                                    <div className="w-full flex items-center gap-4 mb-6">
                                        <div className="h-px bg-zinc-200 dark:bg-zinc-800 flex-1"></div>
                                        <span className="text-[11px] text-zinc-400 font-medium">OR SHARE LINK ONLY</span>
                                        <div className="h-px bg-zinc-200 dark:bg-zinc-800 flex-1"></div>
                                    </div>

                                    <div className="w-full">
                                        <div className="w-full bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded p-2 flex items-center gap-2">
                                            <Input readOnly value={generatedLink} className="flex-1 bg-transparent text-[12px] text-zinc-600 dark:text-zinc-400 border-0 p-2 min-w-0" />
                                            <Button size="sm" variant="secondary" onClick={() => { navigator.clipboard.writeText(generatedLink); alert("Link copied!"); }}><Copy size={12} /></Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-5 border-t border-zinc-200 dark:border-zinc-900 bg-white dark:bg-zinc-950 flex justify-between items-center">
                            <span className="text-[11px] text-zinc-500">Step {step} of 3</span>
                            <div className="flex gap-2">
                                {step > 1 && step < 4 && <Button variant="ghost" onClick={() => setStep(prev => prev - 1 as any)}>Back</Button>}
                                {step < 4 ? (
                                    <Button
                                        onClick={() => handleNextStep(false)}
                                        disabled={step === 1 && (!formData.name.trim() || !formData.receiverName.trim() || !/\S+@\S+\.\S+/.test(formData.receiverEmail.trim()))}
                                    >
                                        Next
                                    </Button>
                                ) : (
                                    <Button onClick={handleLaunch} variant="primary" disabled={isLaunching}>
                                        {isLaunching ? (isEditMode ? 'Sending...' : 'Launching...') : (
                                            <>
                                                <Rocket size={14} className="mr-1.5" />
                                                {isEditMode ? 'Send to receiver' : 'Launch'}
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            <div className="flex-1 overflow-y-auto px-4 md:px-10 pb-20">
                <div className={`hidden md:grid ${showArchived ? 'grid-cols-[40px_3fr_1.5fr_1.2fr_1.2fr_100px]' : 'grid-cols-[3fr_1.5fr_1.2fr_1.2fr_100px]'} gap-4 py-3 text-[10px] font-semibold text-zinc-500 uppercase tracking-wider sticky top-0 bg-white dark:bg-black z-10 transition-colors duration-200`}>
                    {showArchived && (
                        <div className="flex items-center justify-center">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={handleSelectAll}
                                className="w-4 h-4 rounded border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-zinc-500 dark:focus:ring-zinc-400 bg-white dark:bg-zinc-900 cursor-pointer"
                            />
                        </div>
                    )}
                    <div>Project</div>
                    <div>Receiver</div>
                    <div>Status</div>
                    <div>Progress</div>
                    <div className="text-right">Next Due</div>
                </div>

                {selectedProjectIds.size > 0 && showArchived && (
                    <div className="sticky top-[37px] z-10 bg-white dark:bg-black border-b border-zinc-200 dark:border-zinc-900 px-4 py-3 flex items-center justify-between">
                        <span className="text-[13px] text-zinc-600 dark:text-zinc-400">
                            {selectedProjectIds.size} selected
                        </span>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={handleBulkDelete}
                                disabled={isBulkActionLoading}
                            >
                                <Trash2 size={14} className="mr-1.5" /> Delete
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleBulkUnarchive}
                                disabled={isBulkActionLoading}
                            >
                                <Archive size={14} className="mr-1.5" /> Unarchive
                            </Button>
                        </div>
                    </div>
                )}

                {visibleProjects.length === 0 ? (
                    <div className="py-20 text-center text-zinc-500 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-lg">No projects found.</div>
                ) : visibleProjects.map(p => (
                    <ListRow
                        key={p.id}
                        project={p}
                        onSelect={onSelectProject}
                        isSelected={selectedProjectIds.has(p.id)}
                        onToggleSelect={handleToggleSelect}
                        showCheckbox={showArchived}
                    />
                ))}
            </div>

            <Modal
                isOpen={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                title="Delete Projects"
            >
                <div className="space-y-4">
                    <p className="text-[14px] text-zinc-600 dark:text-zinc-400">
                        Delete {selectedProjectIds.size} project{selectedProjectIds.size !== 1 ? 's' : ''}? This action cannot be undone.
                    </p>
                    <div className="flex items-center justify-end gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => setIsDeleteConfirmOpen(false)}
                            disabled={isBulkActionLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleConfirmBulkDelete}
                            disabled={isBulkActionLoading}
                            isLoading={isBulkActionLoading}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};